import { type FormEvent, type KeyboardEvent, useMemo } from 'react';
import styled from 'styled-components';

import { GOAL_AMOUNT_MAX, parseAmount } from '../model';

import { BaseMaskedInput } from './base-primitives.styles';

type CurrencyAmountInputVariant = 'compact' | 'default' | 'amount';

type CurrencyAmountInputProps = {
  ariaLabel?: string;
  className?: string;
  currencyPrefix: string;
  dataUi?: string;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  onValueChange: (nextValue: string) => void;
  textAlign?: 'left' | 'center';
  thousandsSeparator: string;
  value: string;
  variant?: CurrencyAmountInputVariant;
};

function createCurrencyValueMaskBlocks(thousandsSeparator: string) {
  return {
    amount: {
      max: GOAL_AMOUNT_MAX,
      mapToRadix: ['.'],
      mask: Number,
      normalizeZeros: true,
      padFractionalZeros: false,
      radix: ',',
      scale: 0,
      signed: false,
      thousandsSeparator,
    },
  };
}

function normalizeCurrencyAmountInputValue(value: string): string {
  return value.replace(/\D/g, '');
}

const FieldMaskedInput = styled(BaseMaskedInput).attrs<{
  $dataUi?: string;
}>(({ $dataUi }) => ({
  'data-ui': $dataUi,
}))<{
  $textAlign: 'left' | 'center';
  $variant: CurrencyAmountInputVariant;
}>`
  flex: 1;
  min-width: 0;
  margin: 0;
  display: block;
  font-family: ${({ $variant }) =>
    $variant === 'amount' ? 'Inter, sans-serif' : 'inherit'};
  font-size: ${({ $variant, theme }) => {
    if ($variant === 'compact') {
      return theme.scale.px(12);
    }

    if ($variant === 'amount') {
      return theme.scale.px(16);
    }

    return theme.scale.px(16);
  }};
  line-height: ${({ $variant, theme }) => {
    if ($variant === 'compact') {
      return theme.scale.px(15);
    }

    if ($variant === 'amount') {
      return theme.scale.px(20);
    }

    return theme.scale.px(30);
  }};
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: ${({ $textAlign }) => $textAlign};

  &:focus-visible {
    outline: none;
  }
`;

export function CurrencyAmountInput({
  ariaLabel,
  className,
  currencyPrefix,
  dataUi,
  onKeyDown,
  onValueChange,
  textAlign = 'left',
  thousandsSeparator,
  value,
  variant = 'default',
}: CurrencyAmountInputProps) {
  const rawValue = parseAmount(value);
  const valueMaskBlocks = useMemo(
    () => createCurrencyValueMaskBlocks(thousandsSeparator),
    [thousandsSeparator],
  );

  function handleInput(event: FormEvent<HTMLInputElement>) {
    onValueChange(normalizeCurrencyAmountInputValue(event.currentTarget.value));
  }

  return (
    <FieldMaskedInput
      aria-label={ariaLabel}
      $dataUi={dataUi}
      $textAlign={textAlign}
      $variant={variant}
      className={className}
      value={rawValue === null ? '' : String(rawValue)}
      inputMode="numeric"
      mask={`${currencyPrefix}amount`}
      unmask={true}
      lazy={false}
      blocks={valueMaskBlocks}
      onAccept={(_nextValue, maskRef) =>
        onValueChange(String(maskRef.unmaskedValue))
      }
      onInput={handleInput}
      onKeyDown={onKeyDown}
    />
  );
}
